import {
    Box,
    Card,
    CardContent,
    Typography,
    Skeleton,
    FormControl,
    Select,
    MenuItem,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import { FC } from 'react';
import { Program } from '../../../../api/interfaces/Program';
import { NewUserStat } from '../../../../api/interfaces/Dashboard';
import { NO_DATA_TO_DISPLAY_URL } from '../../../../utils/constants';
import { NewUserTableSkeleton } from '../../../../components/skeleton/NewUserTableSkeleton/NewUserTableSkeleton';

interface Props {
    isDashboardLoading: boolean;
    isNewUsersFilterLoading: boolean;
    selectedUsersFilterProgram: string;
    handleUsersFilterByProgramChange: (e: any) => void;
    programs: Program[];
    overviewNewUsers: NewUserStat[];
}

const NewUsersCard: FC<Props> = ({
    isDashboardLoading,
    isNewUsersFilterLoading,
    selectedUsersFilterProgram,
    handleUsersFilterByProgramChange,
    programs,
    overviewNewUsers,
}) => {
    return (
        <Card sx={{ height: 350 }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        New Users
                    </Typography>

                    {isDashboardLoading ? (
                        <Skeleton variant="text" width="30%" height={50} />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 400, color: '#818181' }}>
                                view by
                            </Typography>
                            <FormControl size="small">
                                <Select
                                    value={selectedUsersFilterProgram}
                                    onChange={handleUsersFilterByProgramChange}
                                    defaultValue="all programs"
                                    displayEmpty
                                    disabled={isNewUsersFilterLoading}
                                    inputProps={{ 'aria-label': 'Select Program' }}
                                    sx={{
                                        minWidth: 150,
                                        fontSize: '12px',
                                        color: '#818181',
                                        '.MuiSelect-icon': {
                                            color: '#818181',
                                        },
                                    }}
                                    MenuProps={{
                                        disableScrollLock: true,
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">all programs</MenuItem>
                                    {programs.map((program) => (
                                        <MenuItem key={program._id} value={program._id}>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: 'block',
                                                }}
                                            >
                                                {program.name}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    {(isDashboardLoading || isNewUsersFilterLoading) ? (
                        <NewUserTableSkeleton />
                    ) : (
                        <TableContainer
                            component={Paper}
                            sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '270px' }}
                        >
                            <Table stickyHeader size="medium" aria-label="scrollable table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: '12px' }}>Full Name</TableCell>
                                        <TableCell sx={{ fontSize: '12px' }}>Email Address</TableCell>
                                        <TableCell sx={{ fontSize: '12px' }}>Program</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {overviewNewUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ placeItems: 'center' }}>
                                                <Box
                                                    sx={{
                                                        height: '170px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexDirection: 'column',
                                                        color: '#818181',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={NO_DATA_TO_DISPLAY_URL}
                                                        alt="No data"
                                                        sx={{
                                                            width: '260px',
                                                            maxWidth: '100%',
                                                            objectFit: 'contain',
                                                            mb: 1,
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        overviewNewUsers.map((row) => (
                                            <TableRow
                                                key={row.fullName}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row" sx={{ fontSize: '12px', color: '#818181' }}>
                                                    {row.fullName || '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '12px', color: '#818181' }}>
                                                    {row.email}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '12px', color: '#818181' }}>
                                                    {row.programName || '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default NewUsersCard;
